import { useCallback, useMemo, useState, type ReactNode } from "react";
import { ToastContext, type ToastTone as Tone } from "./toast-context";
import styles from "./toast.module.css";

interface Toast {
  id: number;
  text: string;
  tone: Tone;
}

/** Short confirmations and errors, in the corner on a wide screen, above the tab bar on a phone. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Tone = "info") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    // Errors stay a little longer: they usually say what to do next.
    window.setTimeout(
      () => setToasts((t) => t.filter((x) => x.id !== id)),
      tone === "error" ? 8000 : 5000,
    );
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div aria-live="polite" className={styles.stack}>
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.tone === "error" ? "alert" : "status"}
            className={`zh-glass ${styles.toast} ${styles[t.tone]}`}
          >
            <span className={styles.mark} aria-hidden="true" />
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
